// src/pages/Payments/components/PaymentSummary.tsx
import React from "react";
import { DualRing } from "react-spinners-css";
import CardSection from "../../../components/ui/CardSection";
import ChoiceRow from "../../../components/ui/ChoiceRow";
import SummaryRow from "../../../components/ui/SummaryRow";
import Button from "../../../components/ui/Button";
import { baseColors } from "../../../themes/tokens";
import { IStore, ICart, IPayment, ISaleForm, VoucherType } from "../../../classes/interfaces";
import { ValidateVoucherResult } from "../../../services/paymentsService";
import { getCartChannel } from "../../../services/cartService";
import { FreeShippingBanner, Actions, SpinnerWrap } from "./PaymentSummary.styles";
import { formatBRL } from "../../../helpers/currency.helper";

interface PaymentSummaryProps {
  store: IStore;
  cart: ICart;
  formValues: ISaleForm;
  orderTotal: number;
  voucher: ValidateVoucherResult | null;
  paymentMethods: IPayment[];
  isSending: boolean;
  onSelectMoneyPayment: () => void;
  onSelectNonMoneyPayment: (payment: IPayment) => void;
  onAddMoreItems: () => void;
  onFinalizeOrder: () => void;
}

export default function PaymentSummary({
  store,
  cart,
  formValues,
  orderTotal,
  voucher,
  paymentMethods,
  isSending,
  onSelectMoneyPayment,
  onSelectNonMoneyPayment,
  onAddMoreItems,
  onFinalizeOrder,
}: PaymentSummaryProps) {
  // /menu e cardapio-vitrine: nunca fecha nem compartilha venda, independente da
  // configuracao da loja. Em /store quem manda e o styleSale da loja.
  const isMenuChannel = getCartChannel(cart) === "menu";

  const deliveryFeeLabel =
    formValues.address === "" && store.distances.length > 0
      ? "Variável"
      : (store.freeShippingFrom > 0 && cart.total >= store.freeShippingFrom) ||
          voucher?.voucher.type === VoucherType.Delivery
        ? "Gratuito"
        : formValues.deliveryType === "delivery"
          ? formatBRL(formValues.deliveryFee)
          : "~";

  return (
    <>
      {store.freeShippingFrom > 0 && (
        <FreeShippingBanner $achieved={cart.total > store.freeShippingFrom}>
          <strong>Frete Gratuito</strong>
          <span>Nas compras acima de {formatBRL(store.freeShippingFrom)} ganhe frete grátis.</span>
        </FreeShippingBanner>
      )}

      <CardSection icon="receipt" title="Resumo">
        <SummaryRow label="Total do pedido" value={formatBRL(cart.total)} />
        <SummaryRow label="Taxa de Entrega" value={deliveryFeeLabel} />
        <SummaryRow label="Total a pagar" value={formatBRL(orderTotal)} />
      </CardSection>

      <CardSection icon="card" title="Forma de Pagamento" sub="Pague na entrega ou na retirada">
        {paymentMethods?.map((payment, index) =>
          payment.money ? (
            <ChoiceRow
              key={payment.id}
              icon="money"
              title={payment.name}
              sub={
                formValues.changeFor !== ""
                  ? `Troco para ${formatBRL(formValues.changeFor)} · Troco: ${formatBRL(
                      Number.parseFloat(formValues.changeFor) - orderTotal,
                    )}`
                  : undefined
              }
              selected={cart.payments.some((pay) => pay.id === payment.id)}
              onClick={onSelectMoneyPayment}
              last={index === paymentMethods.length - 1}
            />
          ) : (
            <ChoiceRow
              key={payment.id}
              icon="card"
              title={payment.name}
              selected={cart.payments.some((pay) => pay.id === payment.id)}
              onClick={() => onSelectNonMoneyPayment(payment)}
              last={index === paymentMethods.length - 1}
            />
          ),
        )}
      </CardSection>

      <Actions>
        <Button variant="ghost" size="lg" full onClick={onAddMoreItems}>
          Adicionar mais itens
        </Button>
        {!isMenuChannel && cart.styleSale === "share" && (
          <Button variant="soft" size="lg" full>
            Compartilhar
          </Button>
        )}
        {!isMenuChannel &&
          cart.styleSale === "sale" &&
          (!isSending ? (
            <Button variant="primary" size="lg" full onClick={onFinalizeOrder}>
              Finalizar Compra
            </Button>
          ) : (
            <SpinnerWrap>
              <DualRing color={baseColors.teal} />
            </SpinnerWrap>
          ))}
      </Actions>
    </>
  );
}
